/**
 * TaskBoard（タスクシート翻訳共有アプリ、姉妹プロジェクト
 * https://ngr2000.github.io/TaskBoard/ ）のタスクシートから、大会側が
 * 設定したゴールを取り込む。
 *
 * TaskBoardのターゲット座標（例 "5313/0376"）は、GV4D自身の「グリッド
 * コード」表記（現在の100km区画内を東距・北距それぞれ4桁 or 5桁、10m
 * 単位で表したもの）と同じ形式（実測で確認済み: "5313/0376" はクロスノの
 * 既知ゴールCLP1と同じ座標にデコードできる）。区切りの"/"を外すだけで
 * `resolveEightDigit`/`resolveGridCode`（../goals.ts）にそのまま渡せるため、
 * 座標変換ロジックは新規実装しない。
 */

import type { MarkPoint } from '@gv4d/core';

import { makeGoal, resolveEightDigit, resolveGridCode, type DeclaredGoal } from '../goals.ts';

export interface TaskBoardFlightSummary {
  key: string;
  label: string;
  date: string;
  updatedAt: string;
  taskCount: number;
  competitionName: string;
  /** アーカイブ（保管）された日時。まだアーカイブされていなければ空文字列 */
  archived?: string;
}

export interface TaskBoardFlightsResponse {
  ok: boolean;
  flights: TaskBoardFlightSummary[];
}

export interface TaskBoardFlightResponse {
  ok: boolean;
  key: string;
  /** さらにJSON.parseが要る、タスクシート本体のJSON文字列 */
  data: string;
  updatedAt: string;
}

interface TaskBoardTarget {
  coordinates?: unknown;
  altitude?: unknown;
  mma?: unknown;
  name?: unknown;
  note?: unknown;
}

interface TaskBoardField {
  label?: unknown;
  value?: unknown;
}

interface TaskBoardTask {
  taskNo?: unknown;
  taskId?: unknown;
  targets?: unknown;
  fields?: unknown;
  cancelled?: unknown;
}

export interface TaskBoardFlightData {
  tasks?: unknown;
}

export interface TaskBoardImportResult {
  goals: DeclaredGoal[];
  /** 読み取れなかった項目（1件の異常で全体を止めないよう、続行しつつ積む） */
  skipped: Array<{ name: string; reason: string }>;
}

/**
 * "5313/0376" のような文字列から、東距・北距それぞれの数字列を取り出す。
 * TaskBoard自身の区切り許容（/ 空白 , -）に合わせる。各辺は4桁
 * （既定）か5桁（ゾーン境界向けの拡張表記）のみ対応——`resolveGridCode`
 * が扱えるのがこの2種類のため、それ以外の桁数は読み取れないものとして
 * 扱う。
 */
function extractDigitPair(coordinates: string): { easting: string; northing: string } | null {
  const m = /(\d{3,6})\s*[/\s,-]\s*(\d{3,6})/.exec(coordinates);
  if (!m) return null;
  const [, easting, northing] = m;
  if ((easting.length !== 4 && easting.length !== 5) || (northing.length !== 4 && northing.length !== 5)) {
    return null;
  }
  return { easting, northing };
}

/**
 * TaskBoardの座標（"1886/9520" 等）を緯度経度に直す。グリッド表記なので、
 * 基準点（選択中の会場、goals.tsのvenueReferencePoint）から最も近い区画に決める。
 * 取込み（parseTaskBoardFlight）と、タスクシートで座標をタップしたときの
 * 地図への表示（taskboard-panel.ts）で共通に使う。
 */
export function resolveTaskBoardCoordinates(
  coordinates: string,
  ref: { lat: number; lon: number },
):
  | { ok: true; lat: number; lon: number; code: string; eastingDigits: number }
  | { ok: false; reason: string } {
  const pair = extractDigitPair(coordinates);
  if (!pair) return { ok: false, reason: '座標を読み取れません' };
  const code = pair.easting + pair.northing;
  const wideEasting = pair.easting.length === 5;
  const wideNorthing = pair.northing.length === 5;
  const p = !wideEasting && !wideNorthing ? resolveEightDigit(code, ref) : resolveGridCode(code, ref, wideEasting, wideNorthing);
  if (!p) return { ok: false, reason: '座標を解決できません' };
  return { ok: true, lat: p.lat, lon: p.lon, code, eastingDigits: pair.easting.length };
}

/** "R50m"・"20m" 等（"R"の有無どちらも来る）から半径[m]を取り出す。取込み・地図表示で共通 */
export function taskBoardRadiusM(mma: unknown): number | null {
  return parseRadiusM(mma);
}

/** "915ft" 等から数字部分を取り出す */
function parseAltFt(altitude: unknown): number | null {
  if (typeof altitude !== 'string') return null;
  const m = /(\d+(?:\.\d+)?)\s*ft/i.exec(altitude);
  return m ? Math.round(Number(m[1])) : null;
}

/** "R50m"・"20m" 等（"R"の有無どちらも来る）から半径[m]を取り出す */
function parseRadiusM(mma: unknown): number | null {
  if (typeof mma !== 'string') return null;
  const m = /(\d+(?:\.\d+)?)\s*m/i.exec(mma);
  return m ? Number(m[1]) : null;
}

/**
 * ゴール番号（会場のゴール一覧にある名前。佐賀・一関は3桁）を指していそうな項目の名前。
 * 「Available targets」（HWZ）のように、ゴール・ターゲットを選ばせる項目を拾う。
 * 「Declarations: 2 goals」「Min / Max from CLP」などの宣言の条件は、名前が合わないので拾わない
 */
const GOAL_FIELD_LABEL = /target|goal|ゴール|ターゲット/i;

/**
 * 項目の名前に target/goal を含んでいても、ゴールを選ぶ項目ではないもの
 * （「Min. distances of declared goal(s) from any target … = 500 m」の 500 を、ゴール500と読み違えない）
 */
const GOAL_FIELD_EXCLUDED = /distance|min\.|max\.|minimum|maximum|number of|\bno\. of|距離|最小|最大|個数/i;

/** 数字に単位が付いたもの（"500 m"、"3 km"、"1000ft"）。ゴール番号ではないので、読む前に除く */
const NUMBER_WITH_UNIT = /\d+(?:\.\d+)?\s*(?:km|kt|kts|nm|ft|min|sec|m|s|%)(?![a-z0-9])/gi;

/**
 * MDT（ミニマム・ディスタンス）の「基準点」の項目の名前。MDT には目標のゴールが無く、
 * 最短の時間・距離を飛んだあとに**基準点の近くへマーカーを落とし、基準点までの距離が小さいほど良い**
 * （AXMER 15.13）ので、基準点が採点の中心になる。値はグリッド表記の座標（"1600 / 8000"）
 */
const REFERENCE_POINT_LABEL = /reference\s*point|基準点/i;

/**
 * 値（"365 / 369 / 371" 等）から、会場のゴール一覧にある番号を取り出す。
 * - 区切り（空白 / , 、 ・ & -）で分けた1つ1つが、会場のゴールの名前と**そのまま**一致したものだけ
 * - 3桁の番号なのに会場のゴール一覧に無いものは`missing`に入れる（会場の選び違い・ゴール一覧の
 *   未整備に気づけるよう、黙って捨てない）
 */
function goalNumbersIn(value: string, byName: Map<string, MarkPoint>): { found: MarkPoint[]; missing: string[] } {
  const found: MarkPoint[] = [];
  const missing: string[] = [];
  const seen = new Set<string>();
  for (const token of value.replace(NUMBER_WITH_UNIT, ' ').split(/[\s/,、・&-]+/)) {
    if (!token || seen.has(token)) continue;
    const m = byName.get(token.toUpperCase());
    if (m) {
      seen.add(token);
      found.push(m);
    } else if (/^\d{3}$/.test(token)) {
      seen.add(token);
      missing.push(token);
    }
  }
  return { found, missing };
}

/**
 * TaskBoardの1タスクシートぶん（`?action=flight`の内側JSONをパースした
 * もの）から、宣言ゴール（`DeclaredGoal`）の一覧を作る。
 *
 * 鍵（`key`）は`taskboard:<flightKey>:<taskNo>:<targetIndex>`という
 * 決定的な値にする（`makeGoal`が払い出すランダム鍵を上書き）。これにより
 * 呼び出し側が「同じflightKeyの前回ぶんを消してから積み直す」だけで、
 * 同じタスクシートを選び直したときに前回のインポート分と自然に入れ替わる
 * （利用者確認済みの挙動）。
 *
 * ゴールの指定は2通りある:
 * - 座標（`targets[].coordinates`、"1636/0523"）… 一関・クロスノのシート
 * - **ゴール番号**（"365 / 369 / 371"）… 佐賀のシート（docs/08 §215）。`targets`ではなく
 *   「Available targets」などの項目（`fields`）に入って届く。`marks`（選択中の会場の
 *   ゴール一覧）の同じ名前の点に決める。`marks`を渡さなければ番号は読まない
 */
export function parseTaskBoardFlight(
  flightData: TaskBoardFlightData,
  flightKey: string,
  ref: { lat: number; lon: number },
  marks: readonly MarkPoint[] = [],
): TaskBoardImportResult {
  const goals: DeclaredGoal[] = [];
  const skipped: Array<{ name: string; reason: string }> = [];
  const tasks = Array.isArray(flightData.tasks) ? flightData.tasks : [];
  const byName = new Map<string, MarkPoint>(marks.map((m) => [m.name.toUpperCase(), m]));
  /** 番号のゴールを足す。座標・番号のどちらから来たものも、鍵は`taskboard:<シート>:<タスク>:<通し>`の形にそろえる */
  const addNumbered = (taskNo: string, taskLabel: string, m: MarkPoint, index: number): void => {
    const goal = makeGoal(`${taskLabel} ${m.name}`, m.lat, m.lon);
    goal.key = `taskboard:${flightKey}:${taskNo}:n${index}`;
    goal.altFt = null;
    goals.push(goal);
  };

  tasks.forEach((rawTask, taskIndex) => {
    const task = rawTask as TaskBoardTask;
    const taskNo = typeof task.taskNo === 'string' || typeof task.taskNo === 'number' ? String(task.taskNo) : String(taskIndex);
    const taskLabel = `${taskNo}${task.taskId ? ` ${task.taskId}` : ''}`;
    if (task.cancelled === true) {
      skipped.push({ name: taskLabel, reason: '中止済みのタスクです' });
      return;
    }
    const targets = Array.isArray(task.targets) ? task.targets : [];
    let numberedIndex = 0;
    targets.forEach((rawTarget, targetIndex) => {
      const target = rawTarget as TaskBoardTarget;
      const label = targets.length > 1 ? `${taskLabel}-${targetIndex + 1}` : taskLabel;
      const coordinates = typeof target.coordinates === 'string' ? target.coordinates : '';
      if (!coordinates) {
        // 座標が無くても、名前や注記がゴール番号なら会場のゴール一覧から決める
        const text = [target.name, target.note].filter((v): v is string => typeof v === 'string').join(' ');
        const { found } = goalNumbersIn(text, byName);
        if (found.length > 0) {
          for (const m of found) addNumbered(taskNo, taskLabel, m, numberedIndex++);
          return;
        }
        skipped.push({ name: label, reason: '座標(coordinates)がありません' });
        return;
      }
      const r = resolveTaskBoardCoordinates(coordinates, ref);
      if (!r.ok) {
        skipped.push({ name: label, reason: `${r.reason}（${coordinates}）` });
        return;
      }
      const goal = makeGoal(label, r.lat, r.lon, r.code, r.eastingDigits);
      goal.key = `taskboard:${flightKey}:${taskNo}:${targetIndex}`;
      goal.altFt = parseAltFt(target.altitude);
      goal.goalRadiusM = parseRadiusM(target.mma);
      goals.push(goal);
    });

    // ゴール番号で指定されたもの（佐賀のシート）: 「Available targets: 365 / 369 / 371」など
    const fields = Array.isArray(task.fields) ? (task.fields as TaskBoardField[]) : [];
    for (const f of fields) {
      if (typeof f.label !== 'string' || typeof f.value !== 'string') continue;
      // MDT の基準点（座標）。ゴール番号とは別に、座標として読む
      if (REFERENCE_POINT_LABEL.test(f.label)) {
        const r = resolveTaskBoardCoordinates(f.value, ref);
        if (r.ok) {
          const goal = makeGoal(`${taskLabel} 基準点`, r.lat, r.lon, r.code, r.eastingDigits);
          goal.key = `taskboard:${flightKey}:${taskNo}:ref`;
          goals.push(goal);
        } else {
          skipped.push({ name: `${taskLabel} 基準点`, reason: `${r.reason}（${f.value}）` });
        }
        continue;
      }
      if (!GOAL_FIELD_LABEL.test(f.label) || GOAL_FIELD_EXCLUDED.test(f.label)) continue;
      const { found, missing } = goalNumbersIn(f.value, byName);
      for (const m of found) addNumbered(taskNo, taskLabel, m, numberedIndex++);
      for (const n of missing) skipped.push({ name: `${taskLabel} ${n}`, reason: '会場のゴール一覧にない番号です' });
    }
  });

  return { goals, skipped };
}

/** ある会場でインポート済みの、この`flightKey`由来のゴールを識別する接頭辞 */
export function taskBoardGoalKeyPrefix(flightKey: string): string {
  return `taskboard:${flightKey}:`;
}
